import React, { useState } from 'react';
import { UserProfile } from '../types';
import { WateenLogo } from './WateenLogo';
import { registerWithSupabase, loginWithSupabase } from '../lib/supabase';
import {
  Sparkles,
  Shield,
  User,
  Lock,
  Unlock,
  Mail,
  Globe,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  RefreshCw,
  ChevronLeft,
} from 'lucide-react';

interface AuthModalProps {
  onLoginSuccess: (user: UserProfile) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState<'login' | 'register' | 'recovery'>('login');

  // Login fields
  const [loginQuery, setLoginQuery] = useState('aymen');
  const [loginPassword, setLoginPassword] = useState('admin123');

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [demo2FAHint, setDemo2FAHint] = useState<string | null>(null);

  // Recovery fields
  const [recoveryIdentifier, setRecoveryIdentifier] = useState('');
  const [recoveryStep, setRecoveryStep] = useState<'request' | 'verify'>('request');
  const [recoveryOtpCode, setRecoveryOtpCode] = useState('');
  const [recoveryNewPassword, setRecoveryNewPassword] = useState('');
  const [recoveryDemoHint, setRecoveryDemoHint] = useState<string | null>(null);
  const [recoveryUsername, setRecoveryUsername] = useState('');
  const [recoverySuccessMsg, setRecoverySuccessMsg] = useState<string | null>(null);

  // Register fields
  const [regUsername, setRegUsername] = useState('');
  const [regDisplayName, setRegDisplayName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regCountry, setRegCountry] = useState('المملكة العربية السعودية');
  const [regBio, setRegBio] = useState('مرحباً بك في مساحتي على وتين ✨');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedUsername, setLockedUsername] = useState('');

  // Instant unlock account
  const handleInstantUnlock = async () => {
    setLoading(true);
    try {
      const targetUser = lockedUsername || loginQuery || 'aymen';
      const res = await fetch('/api/auth/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernameOrEmail: targetUser }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setIsLocked(false);
        setError(null);
        setRecoverySuccessMsg('تم فك قفل الحساب بنجاح! يمكنك الآن تسجيل الدخول مباشرة.');
      } else {
        setError(data.error || 'تعذر فك قفل الحساب');
      }
    } catch {
      setError('تعذر الاتصال بالخادم لفك القفل');
    } finally {
      setLoading(false);
    }
  };

  // Quick switch demo accounts
  const quickSwitch = async (username: string) => {
    setLoading(true);
    setError(null);
    setIsLocked(false);
    setRequires2FA(false);
    const demoPassword = username === 'aymen' ? 'admin123' : 'password123';
    setLoginQuery(username);
    setLoginPassword(demoPassword);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usernameOrEmail: username,
          password: demoPassword,
          deviceName: 'متصفح وتين الفاخر',
          quickLogin: true,
        }),
      });
      const data = await res.json();
      if (res.ok && data.user) {
        onLoginSuccess(data.user);
      } else if (data.requires2FA) {
        setLoginQuery(username);
        setRequires2FA(true);
        setDemo2FAHint(data.demoOtp);
      } else {
        if (data.locked) {
          setIsLocked(true);
          setLockedUsername(username);
        }
        setError(data.error || 'فشل تسجيل الدخول');
      }
    } catch {
      setError('تعذر الاتصال بالخادم، يرجى المحاولة ثانية');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginQuery || !loginPassword) {
      setError('الرجاء إدخال اسم المستخدم وكلمة المرور');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usernameOrEmail: loginQuery,
          password: loginPassword,
          deviceName: 'جلسة متصفح وتين',
          twoFactorCode: requires2FA ? twoFactorCode : undefined,
        }),
      });
      const data = await res.json();

      if (data.requires2FA) {
        setRequires2FA(true);
        setDemo2FAHint(data.demoOtp || null);
        setError(null);
        return;
      }

      if (res.ok && data.user) {
        // Synchronize session with Supabase Auth in background
        const authEmail = data.user.email || `${data.user.username}@wateen.app`;
        loginWithSupabase(authEmail, loginPassword).catch(() => {});
        onLoginSuccess(data.user);
      } else {
        if (data.locked) {
          setIsLocked(true);
          setLockedUsername(data.username || loginQuery);
        }
        setError(data.error || 'اسم المستخدم أو كلمة المرور غير صحيحة');
      }
    } catch {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regUsername || !regDisplayName || !regPassword) {
      setError('اسم المستخدم واسم العرض وكلمة المرور مطلوبة');
      return;
    }

    if (regPassword.length < 6) {
      setError('كلمة المرور يجب أن لا تقل عن 6 أحرف لأمان حسابك');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: regUsername,
          displayName: regDisplayName,
          password: regPassword,
          email: regEmail || undefined,
          country: regCountry,
          bio: regBio,
          avatar: `https://api.dicebear.com/7.x/bottts/svg?seed=${regUsername}`,
        }),
      });
      const data = await res.json();
      if (res.ok && data.user) {
        // Register in Supabase Auth
        const regAuthEmail = data.user.email || `${data.user.username}@wateen.app`;
        registerWithSupabase(regAuthEmail, regPassword, {
          username: data.user.username,
          displayName: data.user.displayName,
        }).catch(() => {});
        onLoginSuccess(data.user);
      } else {
        setError(data.error || 'فشل إنشاء الحساب');
      }
    } catch {
      setError('خطأ أثناء تسجيل الحساب');
    } finally {
      setLoading(false);
    }
  };

  // Request Recovery OTP
  const handleRequestRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryIdentifier) {
      setError('الرجاء إدخال اسم المستخدم أو البريد المسجل');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/recovery/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernameOrEmail: recoveryIdentifier }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecoveryStep('verify');
        setRecoveryDemoHint(data.demoOtp || null);
        setRecoveryUsername(data.username || recoveryIdentifier);
      } else {
        setError(data.error || 'تعذر العثور على الحساب أو تجاوز حد الطلبات');
      }
    } catch {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  // Verify Recovery OTP & Reset Password
  const handleVerifyRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryOtpCode || !recoveryNewPassword) {
      setError('رمز التحقق وكلمة المرور الجديدة مطلوبان');
      return;
    }

    if (recoveryNewPassword.length < 6) {
      setError('كلمة المرور الجديدة يجب أن لا تقل عن 6 أحرف');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/recovery/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: recoveryUsername,
          otpCode: recoveryOtpCode,
          newPassword: recoveryNewPassword,
        }),
      });
      const data = await res.json();
      if (res.ok && data.user) {
        setRecoverySuccessMsg('تم تأمين حسابك بنجاح وإنهاء كافة الجلسات السابقة. جاري الدخول...');
        setTimeout(() => {
          onLoginSuccess(data.user);
        }, 1200);
      } else {
        setError(data.error || 'فشل التحقق من رمز الاستعادة');
      }
    } catch {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#06080d]/90 backdrop-blur-xl font-['Cairo'] overflow-y-auto">
      {/* Background ambient glow circles */}
      <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md bg-[#0e131d]/90 border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-2xl my-auto">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-6">
          <WateenLogo size="lg" showText={true} showTagline={true} className="mb-2" />
          <p className="text-xs text-slate-400 max-w-xs mt-1 leading-relaxed">
            المنصة الفاخرة للتواصل الفوري الآمن، الذكاء الاصطناعي، والخصوصية الرقمية.
          </p>
        </div>

        {/* Mode switcher tabs (when not in recovery) */}
        {mode !== 'recovery' && (
          <div className="flex items-center p-1 bg-[#141b27] border border-slate-800 rounded-2xl mb-6">
            <button
              type="button"
              onClick={() => {
                setMode('login');
                setError(null);
                setRequires2FA(false);
              }}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${
                mode === 'login'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              تسجيل الدخول
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('register');
                setError(null);
                setRequires2FA(false);
              }}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${
                mode === 'register'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              إنشاء هوية وتين جديدة ✨
            </button>
          </div>
        )}

        {/* Error notification */}
        {error && (
          <div className="mb-4 p-3.5 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
              <span className="flex-1 leading-relaxed">{error}</span>
            </div>
            {(isLocked || error.includes('قفل الحساب') || error.includes('مؤقتاً')) && (
              <button
                type="button"
                onClick={handleInstantUnlock}
                disabled={loading}
                className="w-full py-2 px-3 rounded-xl bg-gradient-to-r from-emerald-600/30 to-teal-600/30 hover:from-emerald-600/50 hover:to-teal-600/50 border border-emerald-500/50 text-emerald-300 font-bold text-xs flex items-center justify-center gap-2 transition active:scale-98 shadow-sm cursor-pointer"
              >
                <Unlock className="w-3.5 h-3.5 text-emerald-400" />
                <span>فك قفل الحساب فوراً والدخول 🔓</span>
              </button>
            )}
          </div>
        )}

        {/* Success notification */}
        {recoverySuccessMsg && (
          <div className="mb-4 p-3 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
            <span>{recoverySuccessMsg}</span>
          </div>
        )}

        {/* ======================= */}
        {/* LOGIN FORM */}
        {/* ======================= */}
        {mode === 'login' && (
          <form onSubmit={handleLoginSubmit} className="space-y-3.5">
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1.5">
                اسم المستخدم أو البريد الإلكتروني
              </label>
              <div className="relative flex items-center">
                <span className="absolute right-3.5 text-slate-500 font-mono text-sm">@</span>
                <input
                  type="text"
                  value={loginQuery}
                  onChange={(e) => setLoginQuery(e.target.value)}
                  placeholder="aymen"
                  dir="ltr"
                  className="w-full pl-3 pr-8 py-2.5 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition text-right"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-semibold text-slate-300">
                  كلمة المرور
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMode('recovery');
                    setRecoveryIdentifier(loginQuery);
                    setRecoveryStep('request');
                    setError(null);
                  }}
                  className="text-[11px] text-emerald-400 hover:text-emerald-300 font-medium"
                >
                  نسيت كلمة المرور؟
                </button>
              </div>
              <div className="relative flex items-center">
                <Lock className="w-4 h-4 absolute right-3 text-slate-500" />
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-3 pr-9 py-2.5 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition"
                  required
                />
              </div>
            </div>

            {/* 2FA Challenge Section if triggered */}
            {requires2FA && (
              <div className="p-3 rounded-2xl bg-indigo-950/30 border border-indigo-500/40 space-y-2 animate-in fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-indigo-300 flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-indigo-400" />
                    التحقق بخطوتين (2FA OTP)
                  </span>
                  {demo2FAHint && (
                    <span className="text-[10px] text-indigo-400 bg-indigo-500/20 px-2 py-0.5 rounded-full font-mono">
                      رمز التجربة: {demo2FAHint}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-300">
                  أدخل رمز الأمان المكون من 6 أرقام لتأكيد هويتك وحماية الحساب.
                </p>
                <input
                  type="text"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full py-2 bg-[#101726] border border-indigo-500/50 rounded-xl text-center text-lg font-mono tracking-widest text-white focus:outline-none focus:border-indigo-400"
                  required
                  autoFocus
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 mt-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600 hover:from-emerald-400 hover:to-indigo-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              ) : (
                <>
                  <span>{requires2FA ? 'تأكيد الرمز ودخول وتين' : 'دخول إلى وتين'}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Quick Demo Switcher */}
            <div className="pt-4 border-t border-slate-800/80">
              <span className="block text-[10px] text-slate-400 text-center mb-2.5 font-medium">
                ⚡ تجربة سريعة عبر حسابات العرض الجاهزة:
              </span>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => quickSwitch('aymen')}
                  className="p-2 rounded-xl bg-[#141c2a] hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500/50 flex flex-col items-center text-center transition group"
                >
                  <img
                    src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80"
                    alt="أيمن"
                    className="w-8 h-8 rounded-full object-cover mb-1 ring-1 ring-emerald-400"
                  />
                  <span className="text-[11px] font-bold text-slate-200 group-hover:text-emerald-400">أيمن (المدير)</span>
                  <span className="text-[9px] text-slate-400">@aymen</span>
                </button>

                <button
                  type="button"
                  onClick={() => quickSwitch('sarah')}
                  className="p-2 rounded-xl bg-[#141c2a] hover:bg-violet-950/40 border border-slate-800 hover:border-violet-500/50 flex flex-col items-center text-center transition group"
                >
                  <img
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80"
                    alt="سارة"
                    className="w-8 h-8 rounded-full object-cover mb-1 ring-1 ring-violet-400"
                  />
                  <span className="text-[11px] font-bold text-slate-200 group-hover:text-violet-400">سارة</span>
                  <span className="text-[9px] text-slate-400">@sarah</span>
                </button>

                <button
                  type="button"
                  onClick={() => quickSwitch('omar')}
                  className="p-2 rounded-xl bg-[#141c2a] hover:bg-blue-950/40 border border-slate-800 hover:border-blue-500/50 flex flex-col items-center text-center transition group"
                >
                  <img
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80"
                    alt="عمر"
                    className="w-8 h-8 rounded-full object-cover mb-1 ring-1 ring-blue-400"
                  />
                  <span className="text-[11px] font-bold text-slate-200 group-hover:text-blue-400">عمر (مهندس AI)</span>
                  <span className="text-[9px] text-slate-400">@omar</span>
                </button>
              </div>
            </div>
          </form>
        )}

        {/* ======================= */}
        {/* ACCOUNT RECOVERY FORM */}
        {/* ======================= */}
        {mode === 'recovery' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800/80">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>العودة لتسجيل الدخول</span>
              </button>
              <span className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" />
                استعادة وتأمين الحساب
              </span>
            </div>

            {recoveryStep === 'request' ? (
              <form onSubmit={handleRequestRecovery} className="space-y-3.5">
                <p className="text-xs text-slate-300 leading-relaxed">
                  أدخل اسم المستخدم أو البريد الإلكتروني المرتبط بحسابك لإصدار رمز أمان OTP مؤقت وحيد الاستخدام.
                </p>

                <div>
                  <label className="block text-[11px] font-semibold text-slate-300 mb-1.5">
                    اسم المستخدم أو البريد
                  </label>
                  <input
                    type="text"
                    value={recoveryIdentifier}
                    onChange={(e) => setRecoveryIdentifier(e.target.value)}
                    placeholder="aymen أو بريدك المسجل"
                    className="w-full px-3.5 py-2.5 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-lg transition flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  ) : (
                    <>
                      <span>إرسال رمز الأمان (OTP)</span>
                      <KeyRound className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyRecovery} className="space-y-3.5">
                <div className="p-3 rounded-2xl bg-emerald-950/30 border border-emerald-500/40 text-xs text-emerald-300 flex items-center justify-between">
                  <span>تم توليد رمز الأمان المؤقت (صالح لمرة واحدة)</span>
                  {recoveryDemoHint && (
                    <span className="font-mono bg-emerald-500/20 px-2 py-0.5 rounded font-bold">
                      {recoveryDemoHint}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                    رمز التحقق المؤقت (6 أرقام)
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={recoveryOtpCode}
                    onChange={(e) => setRecoveryOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="w-full py-2 bg-[#141b27] border border-slate-800 rounded-xl text-center text-lg font-mono tracking-widest text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                    كلمة المرور الجديدة (6 أحرف على الأقل)
                  </label>
                  <input
                    type="password"
                    value={recoveryNewPassword}
                    onChange={(e) => setRecoveryNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow-lg transition flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  ) : (
                    <>
                      <span>تأكيد الرمز وحفظ كلمة المرور الجديدة</span>
                      <CheckCircle2 className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}

        {/* ======================= */}
        {/* REGISTER FORM */}
        {/* ======================= */}
        {mode === 'register' && (
          <form onSubmit={handleRegisterSubmit} className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                معرف وتين الدائم (@username)
              </label>
              <div className="relative flex items-center">
                <span className="absolute right-3.5 text-emerald-400 font-mono text-sm">@</span>
                <input
                  type="text"
                  value={regUsername}
                  onChange={(e) => setRegUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="unique_username"
                  dir="ltr"
                  className="w-full pl-3 pr-8 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition text-right"
                  required
                />
              </div>
              <span className="text-[10px] text-slate-400 mt-0.5 block">
                حروف إنجليزية صغيرة وأرقام و _ فقط (3-20 حرفاً).
              </span>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                الاسم المعروض (Display Name)
              </label>
              <input
                type="text"
                value={regDisplayName}
                onChange={(e) => setRegDisplayName(e.target.value)}
                placeholder="مثال: أيمن العتيبي"
                className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  كلمة المرور
                </label>
                <input
                  type="password"
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition"
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  الدولة
                </label>
                <input
                  type="text"
                  value={regCountry}
                  onChange={(e) => setRegCountry(e.target.value)}
                  className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                البريد الإلكتروني (اختياري)
              </label>
              <input
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder="name@example.com"
                dir="ltr"
                className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition text-right"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                نبذة عنك (Bio)
              </label>
              <textarea
                value={regBio}
                onChange={(e) => setRegBio(e.target.value)}
                rows={2}
                placeholder="أخبر أصدقاءك عن اهتماماتك..."
                className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 mt-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600 hover:from-emerald-400 hover:to-indigo-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>تأكيد الهوية والانضمام إلى وتين</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* Privacy Note */}
        <div className="mt-5 pt-3 border-t border-slate-800/80 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <Shield className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <span>هويتك وبياناتك مشفرة ومحمية بالكامل داخل خوادم وتين</span>
        </div>
      </div>
    </div>
  );
};
